// Upload a ULG for analysis. Two roads to the analyst's store:
//
//   direct   the browser can reach the box itself (tailnet HTTPS). The API mints a
//            five-minute token, the page probes the box's /health, and the bytes go
//            straight there. Heroku's router never sees a 200 MB body.
//   api      through /api/logs/upload, which streams it on. Always works; slower.
//
// The probe decides per upload and the fallback is silent: nothing went wrong when
// the box is simply not reachable from here. Both roads end in the same record.
import config from '../config';
import { getAccessToken, apiFetchAuthed } from '../apiHelpers';

export interface UploadResult {
  log_id: string;
  recorded: boolean;
  message: string;
  via: 'direct' | 'api';
  drone_serial?: string | null;
  firmware_branch?: string;
}

interface TokenReply { direct_url: string | null; token?: string; expires_in?: number }

const PROBE_MS = 1500;

async function probe(directUrl: string): Promise<boolean> {
  const controller = new AbortController();
  const t = setTimeout(() => controller.abort(), PROBE_MS);
  try {
    const r = await fetch(`${directUrl}/health`, { signal: controller.signal, mode: 'cors' });
    return r.ok;
  } catch {
    return false;
  } finally {
    clearTimeout(t);
  }
}

function postWithProgress(url: string, form: FormData, headers: Record<string, string>, onProgress?: (pct: number) => void, withCredentials = false): Promise<{ status: number; body: any }> {
  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.open('POST', url);
    Object.entries(headers).forEach(([k, v]) => xhr.setRequestHeader(k, v));
    // Credentials only toward our own API. The analyst box answers CORS with a
    // wildcard origin, and a browser refuses a credentialed request to one; the
    // direct road carries its token in a header and needs no cookie.
    xhr.withCredentials = withCredentials;
    xhr.upload.onprogress = (e) => { if (e.lengthComputable && onProgress) onProgress(Math.round((e.loaded / e.total) * 100)); };
    xhr.onerror = () => reject(new Error('Network error during upload'));
    xhr.onload = () => {
      let body: any = null;
      try { body = JSON.parse(xhr.responseText); } catch { body = { raw: xhr.responseText }; }
      resolve({ status: xhr.status, body });
    };
    xhr.send(form);
  });
}

const ULOG_MAGIC = [0x55, 0x4c, 0x6f, 0x67, 0x01, 0x12, 0x35];

/** What a file is by its first bytes: QGC renames an encrypted Kiwi pair to .ulg. */
async function classify(file: File): Promise<'ulog' | 'key' | 'ciphertext' | 'empty'> {
  const head = new Uint8Array(await file.slice(0, 16).arrayBuffer());
  if (head.length === 0) return 'empty';
  if (ULOG_MAGIC.every((b, i) => head[i] === b)) return 'ulog';
  if (String.fromCharCode(...head.slice(0, 7)) === 'ULogKey') return 'key';
  return 'ciphertext';
}

/**
 * One flight: a plain .ulg, or an encrypted Kiwi log with its key file (two
 * files, picked or dropped together). The box decrypts; the key never leaves it.
 */
export async function uploadUlg(input: File | File[], opts: { droneSerial?: string; notes?: string; onProgress?: (pct: number) => void; onRoad?: (road: 'direct' | 'api') => void } = {}): Promise<UploadResult> {
  const files = (Array.isArray(input) ? input : [input]).filter(f => /\.ulg[ck]?$/i.test(f.name));
  if (files.length === 0) throw new Error('Only .ulg files can be analyzed (or an encrypted .ulgc with its .ulgk)');
  if (files.length > 2) throw new Error('One flight at a time: a .ulg, or an encrypted log with its key file');
  const kinds = await Promise.all(files.map(classify));
  const keyIndex = kinds.indexOf('key');
  const key = keyIndex >= 0 ? files[keyIndex] : undefined;
  const file = files.find((_, i) => i !== keyIndex);
  if (!file) throw new Error('That is the key file. Pick the encrypted log together with it.');
  if (kinds[files.indexOf(file)] === 'ciphertext' && !key) {
    throw new Error('This log is encrypted. Pick it together with its key file (the small .ulgk, about 302 bytes; QGC may have renamed it .ulg).');
  }

  // Ask for the direct road first; a box without one answers direct_url: null.
  let tokenReply: TokenReply = { direct_url: null };
  try {
    const r = await apiFetchAuthed('/analysis/upload-token', { method: 'POST' });
    if (r.ok) tokenReply = await r.json();
  } catch { /* the API road still works */ }

  if (tokenReply.direct_url && tokenReply.token && await probe(tokenReply.direct_url)) {
    opts.onRoad?.('direct');
    const form = new FormData();
    form.append('file', file, file.name);
    if (key) form.append('key', key, key.name);
    if (opts.droneSerial) form.append('drone_serial', opts.droneSerial);
    if (opts.notes) form.append('notes', opts.notes);
    try {
      const { status, body } = await postWithProgress(`${tokenReply.direct_url}/logs`, form, { 'x-upload-token': tokenReply.token }, opts.onProgress);
      if (status >= 200 && status < 300) {
        return {
          log_id: body.id, recorded: !!body.recorded, via: 'direct',
          message: body.recorded ? 'Stored on the analyst and recorded' : 'Stored on the analyst',
          drone_serial: body.drone_serial, firmware_branch: body.firmware_branch,
        };
      }
      // A refusal from the box is a real answer (full store, not a ULog); a transport
      // failure is not, and the API road gets its turn.
      if (status === 413 || status === 415 || status === 507 || status === 401) {
        throw new Error(body?.detail || `Upload refused (${status})`);
      }
    } catch (err: any) {
      if (err?.message && !/Network error/.test(err.message)) throw err;
      // the box did not answer the POST: fall through to the API road
    }
  }

  opts.onRoad?.('api');
  const form = new FormData();
  form.append('file', file, file.name);
  if (key) form.append('key', key, key.name);
  if (opts.droneSerial) form.append('drone_serial', opts.droneSerial);
  if (opts.notes) form.append('notes', opts.notes);
  const token = getAccessToken();
  const { status, body } = await postWithProgress(`${config.API_ENDPOINT}/logs/upload`, form, token ? { Authorization: `Bearer ${token}` } : {}, opts.onProgress, true);
  if (status >= 200 && status < 300) {
    return {
      log_id: body.flight_review_log_id, recorded: !!body.recorded, via: 'api',
      message: body.message || 'Uploaded', drone_serial: body.drone_serial, firmware_branch: body.firmware_branch,
    };
  }
  throw new Error(body?.error?.message || body?.detail || `Upload failed (${status})`);
}
